import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Clinic } from '@/api/clinics';
import { eliminateSite, Site } from '@/api/sites';
import { useGetSitesByClinic } from '@/hooks/api/use-get-sites-by-clinic';
import { formatId } from '@/utils/strings';
import { ALERT_CONFIG, swalert } from '@/libs/sweetalert';

import { Table } from '@/components/ui/table';
import { FetchStates } from '@/components/fetch-states';
import { Button } from '@/components/ui/button';

type SitesTableProps = {
  item: Clinic;
};

export function ClinicSitesTable({ item }: SitesTableProps) {
  const { sites, loading, refetchSites } = useGetSitesByClinic({ clinic_id: item.clinic_id });
  const navigate = useNavigate();
  const { t } = useTranslation();

  const headers = useMemo(
    () => [
      { key: 'site_id', label: t('site-id'), type: 'value' as const, format: formatId },
      { key: 'description', label: t('site-name') },
      { key: 'timezone', label: t('timezone') },
      { key: 'active', label: t('active'), type: 'status' as const },
      {
        key: 'actions',
        type: 'actions' as const,
        edit: (_site: unknown) => {
          const site = _site as Site;
          navigate(`${site.clinic_id}/site/edit/${site.site_id}`);
        },
        delete: async (_site: unknown) => {
          const site = _site as Site;
          await onEliminateSite(site);
        },
      },
    ],
    []
  );

  const onEliminateSite = async (site: Site) => {
    const result = await swalert.fire({
      ...ALERT_CONFIG,
      title: t('eliminate-site-title'),
      html: `<p style="margin-bottom: 1rem;">${t('eliminate-site-description').replace('{site_name}', `<span style="font-weight: bold">${site.description ?? ''}</span>`)}</p>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('yes'),
      cancelButtonText: t('no'),
    });

    if (result.isConfirmed) {
      await eliminateSite({ clinic_id: site.clinic_id ?? '', site_id: site.site_id });
      await refetchSites();
    }
  };

  return (
    <>
      <div className="flex justify-end">
        <Button href={`${item.clinic_id}/site/new`}>
          <Plus />
          {t('new-site-button')}
        </Button>
      </div>
      <FetchStates loading={loading} refetch={refetchSites} empty={!sites?.length}>
        <Table headers={headers} data={sites ?? []} />
      </FetchStates>
    </>
  );
}
